import type { Table } from "dexie";

import { UUIDSchema } from "../../domain";

export interface RepositoryOptions {
  now?: () => string;
  createId?: () => string;
}

export class EntityNotFoundError extends Error {
  readonly entityName: string;
  readonly entityId: string;

  constructor(entityName: string, entityId: string) {
    super(`${entityName} ${entityId} was not found.`);
    this.name = "EntityNotFoundError";
    this.entityName = entityName;
    this.entityId = entityId;
  }
}

export class DuplicateEntityError extends Error {
  readonly entityName: string;
  readonly value: string;

  constructor(entityName: string, value: string) {
    super(`${entityName} '${value}' already exists.`);
    this.name = "DuplicateEntityError";
    this.entityName = entityName;
    this.value = value;
  }
}

export function parseEntityId(id: string): string {
  return UUIDSchema.parse(id);
}

export async function assertLiveReferences<T extends { deletedAt?: string | undefined }>(
  table: Table<T, string>,
  ids: readonly string[],
  entityName: string,
): Promise<void> {
  if (ids.length === 0) return;
  const records = await table.bulkGet([...ids]);
  for (let index = 0; index < ids.length; index += 1) {
    const id = ids[index];
    const record = records[index];
    if (id !== undefined && (record === undefined || record.deletedAt !== undefined)) {
      throw new EntityNotFoundError(entityName, id);
    }
  }
}

export function withoutDeleted<T extends { deletedAt?: string | undefined }>(records: readonly T[]): T[] {
  return records.filter((record) => record.deletedAt === undefined);
}

export function bySortOrderThenName<T extends { sortOrder: number }>(
  getName: (record: T) => string,
): (left: T, right: T) => number {
  return (left, right) =>
    left.sortOrder - right.sortOrder || getName(left).localeCompare(getName(right), undefined, {
      sensitivity: "base",
    });
}
